
/**
 * Splits a menu into multiple pages if it exceeds a certain item count.
 * Used for PDF generation to ensure content fits on pages.
 * 
 * @param {Object} menu - The menu object to paginate
 * @param {number} maxItemsPerPage - Maximum "cost" allowed per page (default around 12-15 items)
 * @returns {Array} - Array of menu objects, each representing a page
 */
export const paginateMenu = (menu, maxItemsPerPage = 12) => {
    if (!menu || !menu.categories || menu.categories.length === 0) {
        return [menu];
    }

    // 1. Flatten the menu structure into a linear list of "renderable units"
    // Sorted to ensure visual consistency
    const sortedCategories = [...menu.categories].sort((a, b) =>
        (a.displayOrder || 0) - (b.displayOrder || 0)
    );

    const flatList = [];
    sortedCategories.forEach(cat => {
        // Add header marker
        flatList.push({ type: 'header', category: cat, cost: 2 });
        // Add items
        if (cat.items && cat.items.length > 0) {
            cat.items.forEach(item => {
                flatList.push({ type: 'item', item: item, category: cat, cost: 1 });
            });
        }
    });

    const pages = [];
    let currentPageItems = [];
    let currentCost = 0;

    for (let i = 0; i < flatList.length; i++) {
        const unit = flatList[i];

        // --- ORPHAN PREVENTION LOGIC ---

        // 1. Header Orphan Check:
        // If this is a header, and the NEXT item (the first item of this category) 
        // would overflow the page, we should break NOW.
        // This prevents a page ending with a lonely category header.
        if (unit.type === 'header' && i + 1 < flatList.length) {
            const nextUnit = flatList[i + 1];
            const costWithHeaderAndItem = currentCost + unit.cost + nextUnit.cost;
            if (costWithHeaderAndItem > maxItemsPerPage) {
                // But wait! Can we squeeze?
                const totalRemaining = flatList.length - i;
                // Only squeeze if fairly small overflow
                const canSqueezeHeader = (totalRemaining <= 4) && (costWithHeaderAndItem + (totalRemaining - 2) <= maxItemsPerPage + 4);

                if (!canSqueezeHeader && currentPageItems.length > 0) {
                    // Force break before the header
                    pages.push(finalizePage(menu, currentPageItems, pages.length));
                    currentPageItems = [];
                    currentCost = 0;
                }
            }
        }

        // --- STANDARD OVERFLOW LOGIC ---

        const wouldOverflow = (currentCost + unit.cost) > maxItemsPerPage;

        if (wouldOverflow) {
            // 2. Squeeze Check:
            // If we are near the end of the entire menu, allowing a small overflow 
            // is better than creating a new page with just 1 or 2 items.
            const remainingAfterThis = flatList.length - (i + 1);
            const canSqueeze = (remainingAfterThis <= 2) && ((currentCost + unit.cost + remainingAfterThis) <= (maxItemsPerPage + 4));

            if (!canSqueeze) {
                // Must break page
                // BUT: Don't break if the current page is empty (implies a single huge item > max, theoretical edge case)
                if (currentPageItems.length > 0) {
                    pages.push(finalizePage(menu, currentPageItems, pages.length));
                    currentPageItems = [];
                    currentCost = 0;
                }
            }
        }

        currentPageItems.push(unit);
        currentCost += unit.cost;
    }

    // Push the final page
    if (currentPageItems.length > 0) {
        pages.push(finalizePage(menu, currentPageItems, pages.length));
    }

    return pages.length > 0 ? pages : [menu];
};

// Helper to reconstruct the nested category structure from flat list
const finalizePage = (originalMenu, flatItems, pageIndex) => {
    const categoriesMap = new Map();
    const categoriesOrder = [];

    flatItems.forEach(unit => {
        const catId = unit.category.id;

        if (!categoriesMap.has(catId)) {
            categoriesMap.set(catId, {
                ...unit.category,
                items: [] // Reset items for this chunk
            });
            categoriesOrder.push(catId);
        }

        if (unit.type === 'item') {
            categoriesMap.get(catId).items.push(unit.item);
        }
    });

    const categories = categoriesOrder.map(id => categoriesMap.get(id));

    return {
        ...originalMenu,
        categories: categories,
        chunkIndex: pageIndex
    };
};

export default paginateMenu;
